import React, { useEffect, useState, useContext } from 'react';
import {
    Card, Col, Row, Button, Typography, Tag, message, Layout, Table, Space, Tooltip,
    Statistic, Modal, Input, Progress, Timeline, Empty, DatePicker, Radio, Select, Tabs
} from 'antd';
import {
    ReloadOutlined, ClockCircleOutlined, SearchOutlined, DesktopOutlined,
    FileOutlined, ThunderboltOutlined, CheckCircleTwoTone, MinusCircleTwoTone,
    PlayCircleOutlined, BugOutlined, CodeOutlined, LeftOutlined, RightOutlined,
    CalendarOutlined, SnippetsOutlined, CopyOutlined, FileAddOutlined,
    FolderAddOutlined, FolderOpenOutlined, ExclamationCircleOutlined,
    UndoOutlined, RedoOutlined, CameraOutlined, HistoryOutlined, SwapOutlined, ClearOutlined
} from '@ant-design/icons';

import dayjs, { Dayjs } from 'dayjs';
import Editor, { DiffEditor } from '@monaco-editor/react';
import { AuthContext } from "../../../config/AuthContext";
import AppSider from "../components/AppSider";

const { Text, Paragraph } = Typography;
const { Content } = Layout;
const { RangePicker } = DatePicker;

interface TelemetrySession {
    id_usuari: number;
    nom: string;
    cognoms: string;
    avatar?: string;
    last_event: string;
    online: boolean;
    last_active: number;
    /** Ediciones en el último heartbeat: >0 = ACTIVO modificando código */
    last_edits: number;
    last_project?: string;
    last_file?: string;
    last_language?: string;
    active_seconds_today: number;
    active_seconds_total: number;
    heartbeats_today: number;
    runs_today?: number;
    debugs_today?: number;
    edits_today?: number;
}

/** Calcula el estado de 3 niveles del alumno */
function getSessionStatus(s: TelemetrySession, isTodaySelected: boolean): 'activo' | 'conectado' | 'desconectado' {
    if (!isTodaySelected || !s.online) return 'desconectado';
    if (s.last_edits > 0) return 'activo';
    return 'conectado';
}

interface TelemetryEvent {
    id: number;
    id_usuari: number;
    project?: string;
    file?: string;
    language?: string;
    focused?: number;
    active?: number;
    edits: number;
    active_seconds: number;
    runs?: number;
    debugs?: number;
    code_snippet?: string;
    event_ts: string;
}

const baseUrl = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
        return `${window.location.protocol}//${window.location.hostname}`;
    }
    return 'http://localhost';
};

function formatDuration(totalSeconds: number): string {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s % 60}s`;
    return `${s}s`;
}

function parseDateSafe(iso?: string): Date | null {
    if (!iso) return null;
    // Si viene en formato "YYYY-MM-DDTHH:mm:ss" o "YYYY-MM-DD HH:mm:ss" sin offset 'Z' o '+/-',
    // los navegadores pueden interpretarlo como UTC.
    // Usamos dayjs que interpreta strings sin offset en la zona horaria local del navegador.
    const dj = dayjs(iso);
    if (!dj.isValid()) return null;
    return dj.toDate();
}

function formatDateTime(iso?: string): string {
    if (!iso) return '—';
    const d = parseDateSafe(iso);
    if (!d) return iso;
    return d.toLocaleString();
}

function relativeFrom(iso?: string, now: number = Date.now()): string {
    if (!iso) return '—';
    const d = parseDateSafe(iso);
    if (!d) return iso;
    const diff = Math.max(0, Math.floor((now - d.getTime()) / 1000));
    if (diff < 60) return `hace ${diff}s`;
    const mins = Math.floor(diff / 60);
    if (mins < 60) return `hace ${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `hace ${hours}h ${mins % 60}m`;
    const days = Math.floor(hours / 24);
    return `hace ${days}d`;
}

export const IdeTelemetryDashboard = () => {
    const { setBreadCrumb } = useContext(AuthContext);
    const [sessions, setSessions] = useState<TelemetrySession[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchName, setSearchName] = useState<string>('');
    const [now, setNow] = useState(Date.now());

    // Detalle de un alumno
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailStudent, setDetailStudent] = useState<TelemetrySession | null>(null);
    const [detailEvents, setDetailEvents] = useState<TelemetryEvent[]>([]);
    const [detailClipboardEvents, setDetailClipboardEvents] = useState<any[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [activeTabKey, setActiveTabKey] = useState<'timeline' | 'clipboard'>('timeline');

    // Filtro por archivo en el modal de detalle
    const [selectedFileFilter, setSelectedFileFilter] = useState<string | null>(null);

    // Modal visor de código
    const [codeModalVisible, setCodeModalVisible] = useState(false);
    const [selectedCodeEvent, setSelectedCodeEvent] = useState<TelemetryEvent | null>(null);
    const [previousCodeSnippet, setPreviousCodeSnippet] = useState<string | null>(null);
    const [codeViewMode, setCodeViewMode] = useState<'current' | 'diff'>('diff');

    // Filtro de fechas
    const [dateMode, setDateMode] = useState<'day' | 'range'>('day');
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
    const [selectedRange, setSelectedRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('day'), dayjs().endOf('day')]);

    useEffect(() => {
        setBreadCrumb([{ title: "Admin" }, { title: "Monitoreo IDE" }], true);
        fetchSessions();

        const interval = setInterval(() => {
            if (!document.hidden) {
                fetchSessions();
            }
        }, 30000);
        const timeInterval = setInterval(() => setNow(Date.now()), 30000);

        return () => {
            clearInterval(interval);
            clearInterval(timeInterval);
        };
    }, []);

    const fetchSessions = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${baseUrl()}:8061/telemetry/admin/sessions`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data: TelemetrySession[] = await response.json();
                setSessions(Array.isArray(data) ? data : []);
            } else {
                message.error("No se pudo cargar la telemetría.");
            }
        } catch (error) {
            console.error(error);
            message.error("Error de red al cargar la telemetría.");
        } finally {
            setLoading(false);
        }
    };

    const openDetail = async (record: TelemetrySession) => {
        setDetailStudent(record);
        setDetailVisible(true);
        setSelectedFileFilter(null);
        setActiveTabKey('timeline');
        setDetailEvents([]);
        setDetailClipboardEvents([]);
        setDetailLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const [evRes, clipRes] = await Promise.all([
                fetch(`${baseUrl()}:8061/telemetry/admin/student/${record.id_usuari}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${baseUrl()}:8061/telemetry/admin/student/${record.id_usuari}/clipboard`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (evRes.ok) {
                const data: TelemetryEvent[] = await evRes.json();
                setDetailEvents(Array.isArray(data) ? data : []);
            }
            if (clipRes.ok) {
                const clipData = await clipRes.json();
                setDetailClipboardEvents(Array.isArray(clipData) ? clipData : []);
            }
        } catch (error) {
            console.error(error);
            message.error("Error de red al cargar el detalle.");
        } finally {
            setDetailLoading(false);
        }
    };


    const isTodaySelected = dateMode === 'day' && selectedDate.isSame(dayjs(), 'day');

    const matchesDateFilter = (isoString?: string) => {
        if (!isoString) return false;
        const d = dayjs(isoString);
        if (!d.isValid()) return false;
        if (dateMode === 'day') {
            return d.isSame(selectedDate, 'day');
        }
        if (selectedRange && selectedRange[0] && selectedRange[1]) {
            return (d.isAfter(selectedRange[0].startOf('day')) || d.isSame(selectedRange[0].startOf('day'))) &&
                   (d.isBefore(selectedRange[1].endOf('day')) || d.isSame(selectedRange[1].endOf('day')));
        }
        return true;
    };

    const onlineCount = isTodaySelected ? sessions.filter(s => s.online).length : 0;
    // ACTIVO = online + last_edits > 0 (modificando código)
    const editingCount = isTodaySelected ? sessions.filter(s => s.online && s.last_edits > 0).length : 0;
    // CONECTADO = online + last_edits === 0
    const connectedCount = isTodaySelected ? sessions.filter(s => s.online && s.last_edits === 0).length : 0;
    const totalTimeToday = sessions.reduce((acc, s) => acc + (Number(s.active_seconds_today) || 0), 0);

    const filteredSessions = sessions.filter(s => {
        if (searchName) {
            const full = `${s.nom} ${s.cognoms}`.toLowerCase();
            if (!full.includes(searchName.toLowerCase())) return false;
        }
        // Si no es hoy, filtramos alumnos que hayan tenido actividad en la fecha seleccionada
        if (!isTodaySelected) {
            return matchesDateFilter(s.last_event);
        }
        return true;
    });

    const displayedDetailEvents = detailEvents.filter(ev => matchesDateFilter(ev.event_ts));

    // Archivos únicos detectados en la sesión del alumno con conteo de eventos y ediciones
    const availableFilesInfo = React.useMemo(() => {
        const fileMap: { [key: string]: { count: number; edits: number; language?: string } } = {};
        displayedDetailEvents.forEach(ev => {
            const f = ev.file || '(Sin archivo)';
            if (!fileMap[f]) {
                fileMap[f] = { count: 0, edits: 0, language: ev.language };
            }
            fileMap[f].count += 1;
            fileMap[f].edits += (ev.edits || 0);
            if (!fileMap[f].language && ev.language) {
                fileMap[f].language = ev.language;
            }
        });
        return fileMap;
    }, [displayedDetailEvents]);

    // Eventos tras filtrar por archivo si el usuario ha seleccionado uno
    const eventsFilteredByFile = React.useMemo(() => {
        if (!selectedFileFilter) return displayedDetailEvents;
        return displayedDetailEvents.filter(ev => {
            const f = ev.file || '(Sin archivo)';
            return f === selectedFileFilter;
        });
    }, [displayedDetailEvents, selectedFileFilter]);

    // Agrupación inteligente de eventos consecutivos sin cambios
    // 3 estados: activo (edits>0 o runs/debugs), conectado (active pero sin edits), desconectado (no active)
    interface GroupedTimelineItem {
        isGroup: boolean;
        representativeEvent: TelemetryEvent;
        count: number;
        startTs: string;
        endTs: string;
        totalActiveSeconds: number;
        /** Estado del evento: activo=modificando código, conectado=IDE abierto sin editar, desconectado=sin IDE */
        eventStatus: 'activo' | 'conectado' | 'desconectado';
        file?: string;
        /** Todos los ficheros distintos tocados en el bloque agrupado (sin modificaciones) */
        files: string[];
        language?: string;
        project?: string;
        hasCodeChange: boolean;
        prevSnippet: string | null;
    }

    /** Calcula el estado de 3 niveles para un evento individual */
    const getEventStatus = (ev: TelemetryEvent): 'activo' | 'conectado' | 'desconectado' => {
        const isOnline = Number(ev.active) === 1 || Number(ev.focused) === 1;
        if (!isOnline) return 'desconectado';
        if ((ev.edits || 0) > 0 || (ev.runs || 0) > 0 || (ev.debugs || 0) > 0) return 'activo';
        return 'conectado';
    };

    const groupedTimelineItems: GroupedTimelineItem[] = React.useMemo(() => {
        const result: GroupedTimelineItem[] = [];
        let i = 0;
        const events = eventsFilteredByFile;

        while (i < events.length) {
            const ev = events[i];
            const evStatus = getEventStatus(ev);
            const hasEditsOrExec = (ev.edits > 0) || ((ev.runs || 0) > 0) || ((ev.debugs || 0) > 0);

            // Buscar si hay cambio real de código respecto al snippet anterior de ese mismo archivo
            let prevSnippet: string | null = null;
            let hasCodeChange = false;

            if (ev.code_snippet) {
                for (let k = i + 1; k < events.length; k++) {
                    if (events[k].code_snippet && (!ev.file || events[k].file === ev.file)) {
                        prevSnippet = events[k].code_snippet || null;
                        break;
                    }
                }
                // Hay cambio real si hay ediciones o si el snippet difiere del anterior
                if (ev.edits > 0 || (prevSnippet !== null && prevSnippet !== ev.code_snippet)) {
                    hasCodeChange = true;
                }
            }

            // Si es ACTIVO (tiene ediciones, runs, debugs o cambio real de código), no agrupamos
            if (hasEditsOrExec || hasCodeChange) {
                result.push({
                    isGroup: false,
                    representativeEvent: ev,
                    count: 1,
                    startTs: ev.event_ts,
                    endTs: ev.event_ts,
                    totalActiveSeconds: ev.active_seconds || 0,
                    eventStatus: evStatus,
                    file: ev.file,
                    files: ev.file ? [ev.file] : [],
                    language: ev.language,
                    project: ev.project,
                    hasCodeChange,
                    prevSnippet
                });
                i++;
                continue;
            }

            // Es CONECTADO o DESCONECTADO sin cambios: agrupar consecutivos del mismo estado
            // (sin importar el fichero — fusionar varios ficheros en un solo bloque)
            const fileSet = new Set<string>();
            if (ev.file) fileSet.add(ev.file);
            let groupCount = 1;
            let totalActiveSecs = ev.active_seconds || 0;
            let lastEventTs = ev.event_ts;
            let j = i + 1;

            while (j < events.length) {
                const nextEv = events[j];
                const nextHasEdits = (nextEv.edits > 0) || ((nextEv.runs || 0) > 0) || ((nextEv.debugs || 0) > 0);
                const nextStatus = getEventStatus(nextEv);

                // Paramos solo si el siguiente tiene actividad real o cambia de estado
                // No paramos por cambio de fichero — los agrupamos en el mismo bloque
                if (nextHasEdits || nextStatus !== evStatus) {
                    break;
                }

                if (nextEv.file) fileSet.add(nextEv.file);
                groupCount++;
                totalActiveSecs += (nextEv.active_seconds || 0);
                lastEventTs = nextEv.event_ts;
                j++;
            }

            result.push({
                isGroup: groupCount > 1,
                representativeEvent: ev,
                count: groupCount,
                startTs: lastEventTs, // eventos DESC → el más antiguo queda al final
                endTs: ev.event_ts,
                totalActiveSeconds: totalActiveSecs,
                eventStatus: evStatus,
                file: ev.file,
                files: Array.from(fileSet),
                language: ev.language,
                project: ev.project,
                hasCodeChange: false,
                prevSnippet: null
            });

            i = j;
        }

        return result;

    }, [eventsFilteredByFile]);

    const columns = [
        {
            title: 'Alumno',
            key: 'alumno',
            render: (_: any, r: TelemetrySession) => (
                <>
                    <strong>{r.nom} {r.cognoms}</strong>{' '}
                    <Text type="secondary">(ID: {r.id_usuari})</Text>
                </>
            ),
            sorter: (a: TelemetrySession, b: TelemetrySession) =>
                `${a.nom} ${a.cognoms}`.localeCompare(`${b.nom} ${b.cognoms}`)
        },
        {
            title: 'Estado',
            key: 'estado',
            render: (_: any, r: TelemetrySession) => {
                const status = getSessionStatus(r, isTodaySelected);
                if (status === 'activo') {
                    return <Tag color="green" icon={<ThunderboltOutlined />}>ACTIVO</Tag>;
                }
                if (status === 'conectado') {
                    return <Tag color="blue" icon={<DesktopOutlined />}>CONECTADO</Tag>;
                }
                return <Tag color="default" icon={<MinusCircleTwoTone twoToneColor="#bbb" />}>DESCONECTADO</Tag>;
            },
            filters: [
                { text: '🟢 Activo (escribiendo código)', value: 'activo' },
                { text: '🔵 Conectado (IDE abierto)', value: 'conectado' },
                { text: '⚫ Desconectado', value: 'desconectado' }
            ],
            onFilter: (value: any, r: TelemetrySession) => {
                return getSessionStatus(r, isTodaySelected) === value;
            }
        },
        {
            title: 'Última actividad',
            key: 'last_event',
            render: (_: any, r: TelemetrySession) => (
                <Tooltip title={formatDateTime(r.last_event)}>
                    <span><ClockCircleOutlined /> {relativeFrom(r.last_event, now)}</span>
                </Tooltip>
            ),
            sorter: (a: TelemetrySession, b: TelemetrySession) =>
                new Date(a.last_event).getTime() - new Date(b.last_event).getTime()
        },
        {
            title: isTodaySelected ? 'Tiempo hoy' : 'Tiempo sesión',
            key: 'today',
            render: (_: any, r: TelemetrySession) => <strong>{formatDuration(r.active_seconds_today)}</strong>,
            sorter: (a: TelemetrySession, b: TelemetrySession) =>
                (a.active_seconds_today || 0) - (b.active_seconds_today || 0)
        },
        {
            title: 'Tiempo total',
            key: 'total',
            render: (_: any, r: TelemetrySession) => formatDuration(r.active_seconds_total),
            sorter: (a: TelemetrySession, b: TelemetrySession) =>
                (a.active_seconds_total || 0) - (b.active_seconds_total || 0)
        },
        {
            title: 'Trabajando en',
            key: 'context',
            render: (_: any, r: TelemetrySession) => (
                <div style={{ fontSize: 12, color: '#666' }}>
                    <div><FileOutlined /> {r.last_file || '—'}</div>
                    <div>
                        {r.last_project ? <Tag>{r.last_project}</Tag> : null}
                        {r.last_language ? <Tag color="blue">{r.last_language}</Tag> : null}
                    </div>
                </div>
            )
        },
        {
            title: isTodaySelected ? 'Ejecuciones hoy' : 'Ejecuciones',
            key: 'runs',
            render: (_: any, r: TelemetrySession) => (
                <Space direction="vertical" size={2}>
                    <Tag color="cyan" icon={<PlayCircleOutlined />}>{r.runs_today || 0} runs</Tag>
                    {(r.debugs_today || 0) > 0 && (
                        <Tag color="magenta" icon={<BugOutlined />}>{r.debugs_today} debugs</Tag>
                    )}
                </Space>
            ),
            sorter: (a: TelemetrySession, b: TelemetrySession) => (a.runs_today || 0) - (b.runs_today || 0)
        },
        {
            title: 'Detalle',
            key: 'actions',
            render: (_: any, r: TelemetrySession) => (
                <Button icon={<SearchOutlined />} onClick={() => openDetail(r)}>Ver historial</Button>
            )
        }
    ];

    const setPnt = (_value: number) => { };

    return (
        <>
            <AppSider currentVal={6} setPantalla={setPnt} />
            <Content style={{ padding: '24px', background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
                {/* Barra de Filtro de Fecha */}
                <Card style={{ marginBottom: 20 }} bodyStyle={{ padding: '14px 20px' }}>
                    <Row align="middle" justify="space-between" gutter={[16, 12]}>
                        <Col>
                            <Space size="middle" wrap direction="horizontal">
                                <Radio.Group
                                    value={dateMode}
                                    onChange={e => setDateMode(e.target.value)}
                                    buttonStyle="solid"
                                >
                                    <Radio.Button value="day">Día</Radio.Button>
                                    <Radio.Button value="range">Rango</Radio.Button>
                                </Radio.Group>

                                {dateMode === 'day' ? (
                                    <Space size="small">
                                        <Button
                                            icon={<LeftOutlined />}
                                            onClick={() => setSelectedDate(d => d.subtract(1, 'day'))}
                                            title="Día anterior"
                                        />
                                        <DatePicker
                                            value={selectedDate}
                                            onChange={d => d && setSelectedDate(d)}
                                            allowClear={false}
                                            format="DD/MM/YYYY"
                                            style={{ width: 140 }}
                                        />
                                        <Button
                                            icon={<RightOutlined />}
                                            onClick={() => setSelectedDate(d => d.add(1, 'day'))}
                                            disabled={selectedDate.isSame(dayjs(), 'day')}
                                            title="Día siguiente"
                                        />
                                        {!isTodaySelected && (
                                            <Button
                                                type="dashed"
                                                size="small"
                                                onClick={() => setSelectedDate(dayjs())}
                                            >
                                                Ir a Hoy
                                            </Button>
                                        )}
                                    </Space>
                                ) : (
                                    <RangePicker
                                        value={selectedRange}
                                        onChange={val => {
                                            if (val && val[0] && val[1]) {
                                                setSelectedRange([val[0], val[1]]);
                                            }
                                        }}
                                        allowClear={false}
                                        format="DD/MM/YYYY"
                                    />
                                )}
                            </Space>
                        </Col>

                        <Col>
                            <Space size="middle">
                                <Input
                                    placeholder="Buscar alumno..."
                                    allowClear
                                    style={{ width: 220 }}
                                    value={searchName}
                                    onChange={e => setSearchName(e.target.value)}
                                    prefix={<SearchOutlined />}
                                />
                                <Button icon={<ReloadOutlined />} onClick={fetchSessions} loading={loading}>
                                    Refrescar
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={6}>
                        <Card style={{ borderLeft: '4px solid #52c41a' }}>
                            <Statistic
                                title={isTodaySelected ? "Activos (escribiendo código)" : "Con actividad"}
                                value={editingCount}
                                prefix={<ThunderboltOutlined style={{ color: '#52c41a' }} />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card style={{ borderLeft: '4px solid #1890ff' }}>
                            <Statistic
                                title={isTodaySelected ? "Conectados (IDE abierto)" : "Con sesión"}
                                value={connectedCount}
                                prefix={<DesktopOutlined style={{ color: '#1890ff' }} />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card style={{ borderLeft: '4px solid #d9d9d9' }}>
                            <Statistic
                                title="Desconectados"
                                value={filteredSessions.length - onlineCount}
                                prefix={<MinusCircleTwoTone twoToneColor="#bbb" />}
                                valueStyle={{ color: '#999' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card style={{ borderLeft: '4px solid #faad14' }}>
                            <Statistic
                                title={isTodaySelected ? "Tiempo total hoy" : "Tiempo acumulado"}
                                value={formatDuration(totalTimeToday)}
                                prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {filteredSessions.length === 0 && !loading ? (
                    <div style={{ textAlign: 'center', marginTop: 80, color: '#999' }}>
                        <DesktopOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <p>Aún no hay telemetría de alumnos para el filtro seleccionado.</p>
                    </div>
                ) : (
                    <Table
                        columns={columns as any}
                        dataSource={filteredSessions}
                        rowKey="id_usuari"
                        loading={loading}
                        pagination={{ pageSize: 15 }}
                    />
                )}

                <Modal
                    title={detailStudent ? `Historial de ${detailStudent.nom} ${detailStudent.cognoms}` : 'Historial'}
                    open={detailVisible}
                    onCancel={() => setDetailVisible(false)}
                    footer={<Button onClick={() => setDetailVisible(false)}>Cerrar</Button>}
                    width={720}
                >
                    {detailStudent && (
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={8}>
                                <Statistic title={isTodaySelected ? "Tiempo hoy" : "Tiempo sesión"} value={formatDuration(detailStudent.active_seconds_today)} />
                            </Col>
                            <Col span={8}>
                                <Statistic title="Tiempo total" value={formatDuration(detailStudent.active_seconds_total)} />
                            </Col>
                            <Col span={8}>
                                <Statistic title="Estado"
                                    valueRender={() => {
                                        const st = getSessionStatus(detailStudent, isTodaySelected);
                                        if (st === 'activo') return <Tag color="green" icon={<ThunderboltOutlined />}>ACTIVO</Tag>;
                                        if (st === 'conectado') return <Tag color="blue" icon={<DesktopOutlined />}>CONECTADO</Tag>;
                                        return <Tag>DESCONECTADO</Tag>;
                                    }} />
                            </Col>
                        </Row>
                    )}

                    {/* Tabs entre Línea de Tiempo e Historial de Copiar/Pegar */}
                    <Tabs
                        activeKey={activeTabKey}
                        onChange={(k: any) => setActiveTabKey(k)}
                        items={[
                            {
                                key: 'timeline',
                                label: <span><ClockCircleOutlined /> Línea de Tiempo</span>,
                                children: (
                                    <>
                                        {/* Filtro y selector de archivos trabajados */}
                                        {Object.keys(availableFilesInfo).length > 0 && (
                                            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                                                <Space size="small">
                                                    <Text strong style={{ fontSize: 13 }}><FileOutlined /> Filtrar por archivo:</Text>
                                                    <Select
                                                        style={{ width: 320 }}
                                                        allowClear
                                                        placeholder="Todos los archivos trabajados"
                                                        value={selectedFileFilter}
                                                        onChange={val => setSelectedFileFilter(val || null)}
                                                    >
                                                        {Object.entries(availableFilesInfo).map(([fname, info]) => (
                                                            <Select.Option key={fname} value={fname}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <span>{fname}</span>
                                                                    <Space size={4}>
                                                                        {info.edits > 0 && <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>{info.edits} edits</Tag>}
                                                                        <Tag style={{ fontSize: 10, margin: 0 }}>{info.count} registros</Tag>
                                                                    </Space>
                                                                </div>
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </Space>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {Object.keys(availableFilesInfo).length} {Object.keys(availableFilesInfo).length === 1 ? 'archivo detectado' : 'archivos detectados'}
                                                </Text>
                                            </div>
                                        )}

                                        {detailLoading ? (
                                            <div style={{ textAlign: 'center', padding: 24 }}>Cargando historial...</div>
                                        ) : groupedTimelineItems.length === 0 ? (
                                            <Empty description="Sin eventos registrados en el filtro seleccionado" />
                                        ) : (
                                            <Timeline
                                                style={{ maxHeight: 440, overflowY: 'auto', paddingTop: 8 }}
                                                items={groupedTimelineItems.map((item, idx) => {
                                                    const ev = item.representativeEvent;
                                                    const st = item.eventStatus;

                                                    const statusColor = st === 'activo' ? 'green' : st === 'conectado' ? 'blue' : 'gray';
                                                    const statusDot = st === 'activo'
                                                        ? <ThunderboltOutlined style={{ color: '#52c41a' }} />
                                                        : st === 'conectado'
                                                            ? <DesktopOutlined style={{ color: '#1890ff' }} />
                                                            : <MinusCircleTwoTone twoToneColor="#bbb" />;
                                                    const statusTag = st === 'activo'
                                                        ? <Tag color="green" icon={<ThunderboltOutlined />}>activo</Tag>
                                                        : st === 'conectado'
                                                            ? <Tag color="blue" icon={<DesktopOutlined />}>conectado</Tag>
                                                            : <Tag>desconectado</Tag>;

                                                    if (item.isGroup || item.files.length > 1) {
                                                        const groupLabel = st === 'activo'
                                                            ? 'activo sin ediciones' : st === 'conectado'
                                                                ? 'conectado sin modificaciones' : 'desconectado';
                                                        return {
                                                            key: `group-${idx}-${item.startTs}`,
                                                            color: statusColor,
                                                            dot: statusDot,
                                                            children: (
                                                                <div style={{ padding: '4px 0' }}>
                                                                    <Space wrap>
                                                                        <Text strong>
                                                                            {formatDateTime(item.startTs)} — {formatDateTime(item.endTs)}
                                                                        </Text>
                                                                        <Tag color={statusColor}>
                                                                            {item.count} intervalos {groupLabel}
                                                                        </Tag>
                                                                        {item.totalActiveSeconds > 0 && (
                                                                            <Tag>{formatDuration(item.totalActiveSeconds)} acumulados</Tag>
                                                                        )}
                                                                    </Space>
                                                                    {item.files.length > 0 && (
                                                                        <div style={{ fontSize: 12, color: '#888', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                                            <FileOutlined style={{ marginRight: 4 }} />
                                                                            {item.files.map(f => (
                                                                                <Tag key={f} style={{ fontSize: 11, margin: 0 }}>{f}</Tag>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )
                                                        };
                                                    }

                                                    return {
                                                        key: `ev-${item.representativeEvent.id || idx}`,
                                                        color: statusColor,
                                                        dot: statusDot,
                                                        children: (
                                                            <div>
                                                                <div>
                                                                    <Text strong>{formatDateTime(ev.event_ts)}</Text>{' '}
                                                                    {statusTag}
                                                                    {ev.edits > 0 && <Tag color="purple">{ev.edits} ediciones</Tag>}
                                                                    {(ev.runs || 0) > 0 && <Tag color="cyan" icon={<PlayCircleOutlined />}>{ev.runs} run</Tag>}
                                                                    {(ev.debugs || 0) > 0 && <Tag color="magenta" icon={<BugOutlined />}>{ev.debugs} debug</Tag>}
                                                                    {ev.active_seconds > 0 && <Tag>{formatDuration(ev.active_seconds)}</Tag>}
                                                                </div>
                                                                <div style={{ fontSize: 12, color: '#666', marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                    <div>
                                                                        <FileOutlined /> {ev.file || '—'}
                                                                        {ev.language ? <Tag color="blue" style={{ marginLeft: 8 }}>{ev.language}</Tag> : null}
                                                                        {ev.project ? <Tag style={{ marginLeft: 4 }}>{ev.project}</Tag> : null}
                                                                    </div>
                                                                    {ev.code_snippet && item.hasCodeChange && (
                                                                        <Space>
                                                                            <Button
                                                                                size="small"
                                                                                type="primary"
                                                                                ghost
                                                                                icon={<CodeOutlined />}
                                                                                onClick={() => {
                                                                                    setPreviousCodeSnippet(item.prevSnippet);
                                                                                    setCodeViewMode(item.prevSnippet ? 'diff' : 'current');
                                                                                    setSelectedCodeEvent(ev);
                                                                                    setCodeModalVisible(true);
                                                                                }}
                                                                            >
                                                                                Ver diff / código
                                                                            </Button>
                                                                        </Space>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    };
                                                })}
                                            />
                                        )}
                                    </>
                                )
                            },
                            {
                                key: 'clipboard',
                                label: (
                                    <span>
                                        <CopyOutlined /> Portapapeles y Archivos
                                        {detailClipboardEvents.length > 0 && (
                                            <Tag color="red" style={{ marginLeft: 6, fontSize: 11, borderRadius: 8, padding: '0 5px' }}>
                                                {detailClipboardEvents.length}
                                            </Tag>
                                        )}
                                    </span>
                                ),
                                children: (
                                    <div style={{ maxHeight: 440, overflowY: 'auto', paddingTop: 8 }}>
                                        {detailClipboardEvents.length === 0 ? (
                                            <Empty description="No se han detectado operaciones de portapapeles ni archivos externos arrastrados para este alumno" />
                                        ) : (
                                            <Table
                                                size="small"
                                                dataSource={detailClipboardEvents}
                                                rowKey="id"
                                                pagination={{ pageSize: 8 }}
                                                columns={[
                                                    {
                                                        title: 'Acción',
                                                        dataIndex: 'action_type',
                                                        key: 'action_type',
                                                        width: 120,
                                                        render: (action: string) => {
                                                            const act = (action || '').toUpperCase();
                                                            if (act === 'FILE_DROP') {
                                                                return (
                                                                    <Tag color="magenta" icon={<FileAddOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        FILE DROP
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'FILE_CREATE') {
                                                                return (
                                                                    <Tag color="cyan" icon={<FileAddOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        NUEVO ARCHIVO
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'FOLDER_CREATE') {
                                                                return (
                                                                    <Tag color="purple" icon={<FolderAddOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        NUEVA CARPETA
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'TAB_CHANGE') {
                                                                return (
                                                                    <Tag color="geekblue" icon={<FileOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        CAMBIO PESTAÑA
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'PROJECT_CHANGE') {
                                                                return (
                                                                    <Tag color="gold" icon={<FolderOpenOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        CAMBIO PROYECTO
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'FOCUS_LOST') {
                                                                return (
                                                                    <Tag color="red" icon={<ExclamationCircleOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        FOCO PERDIDO
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'UNDO') {
                                                                return (
                                                                    <Tag color="default" icon={<UndoOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        UNDO (Ctrl+Z)
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'REDO') {
                                                                return (
                                                                    <Tag color="default" icon={<RedoOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        REDO (Ctrl+Y)
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'WIN_V_CLIPBOARD_HISTORY') {
                                                                return (
                                                                    <Tag color="purple" icon={<HistoryOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        HISTORIAL WIN+V
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'SCREENSHOT_ATTEMPT') {
                                                                return (
                                                                    <Tag color="error" icon={<CameraOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        CAPTURA PANTALLA
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'ALT_TAB_SWITCH') {
                                                                return (
                                                                    <Tag color="volcano" icon={<SwapOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        ALT+TAB
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'PASTE_SPECIAL') {
                                                                return (
                                                                    <Tag color="orange" icon={<SnippetsOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        PEGADO ESPECIAL
                                                                    </Tag>
                                                                );
                                                            }
                                                            if (act === 'CLIPBOARD_RESET_UNLOCK') {
                                                                return (
                                                                    <Tag color="cyan" icon={<ClearOutlined />} style={{ fontWeight: 'bold' }}>
                                                                        RESET TRAS DESBLOQUEO
                                                                    </Tag>
                                                                );
                                                            }
                                                            const color = act === 'PASTE' ? 'orange' : act === 'COPY' ? 'blue' : act === 'CUT' ? 'volcano' : 'green';
                                                            return <Tag color={color} style={{ fontWeight: 'bold' }}>{act}</Tag>;
                                                        }
                                                    },
                                                    {
                                                        title: 'Archivo / Problema',
                                                        dataIndex: 'file',
                                                        key: 'file',
                                                        width: 140,
                                                        render: (f: string) => f ? <Tag color="blue">{f}</Tag> : <Text type="secondary">—</Text>
                                                    },
                                                    {
                                                        title: 'Origen',
                                                        dataIndex: 'source_element',
                                                        key: 'source_element',
                                                        width: 130,
                                                        render: (src: string) => {
                                                            const isExternal = src === 'DRAG_EXTERNAL_FILE' || src === 'THEIA_FILE_SYSTEM';
                                                            return <Tag color={isExternal ? "purple" : "geekblue"}>{src || 'EXAM_FRAME'}</Tag>;
                                                        }
                                                    },
                                                    {
                                                        title: 'Fecha y hora',
                                                        dataIndex: 'event_ts',
                                                        key: 'event_ts',
                                                        width: 150,
                                                        render: (ts: string) => formatDateTime(ts)
                                                    },
                                                    {
                                                        title: 'Contenido / Preview',
                                                        dataIndex: 'content_text',
                                                        key: 'content_text',
                                                        render: (txt: string) => (
                                                            <div style={{
                                                                maxHeight: 70,
                                                                maxWidth: 320,
                                                                overflowY: 'auto',
                                                                background: '#f5f5f5',
                                                                padding: '4px 6px',
                                                                borderRadius: 4,
                                                                fontFamily: 'monospace',
                                                                fontSize: 11,
                                                                whiteSpace: 'pre-wrap',
                                                                wordBreak: 'break-all'
                                                            }}>
                                                                {txt || '(vacío)'}
                                                            </div>
                                                        )
                                                    }
                                                ]}
                                            />
                                        )}
                                    </div>
                                )
                            }
                        ]}
                    />
                </Modal>

                {/* Modal Visor de Código con vista Diff o Completa */}
                <Modal
                    title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginRight: 32 }}>
                            <Space wrap>
                                <CodeOutlined />
                                <span>
                                    <strong>{selectedCodeEvent?.file || 'fichero'}</strong> — {formatDateTime(selectedCodeEvent?.event_ts)}
                                </span>
                                {selectedCodeEvent?.language && <Tag color="blue">{selectedCodeEvent.language}</Tag>}
                                {selectedCodeEvent?.edits ? <Tag color="purple">{selectedCodeEvent.edits} ediciones</Tag> : null}
                            </Space>
                            {previousCodeSnippet && (
                                <Radio.Group
                                    size="small"
                                    value={codeViewMode}
                                    onChange={e => setCodeViewMode(e.target.value)}
                                    buttonStyle="solid"
                                >
                                    <Radio.Button value="diff">Ver Cambios (Diff)</Radio.Button>
                                    <Radio.Button value="current">Código Completo</Radio.Button>
                                </Radio.Group>
                            )}
                        </div>
                    }
                    open={codeModalVisible}
                    onCancel={() => setCodeModalVisible(false)}
                    footer={<Button onClick={() => setCodeModalVisible(false)}>Cerrar</Button>}
                    width={950}
                    bodyStyle={{ padding: 0 }}
                >
                    <div style={{ height: 500, borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
                        {codeViewMode === 'diff' && previousCodeSnippet ? (
                            <DiffEditor
                                height="100%"
                                language={(selectedCodeEvent?.language || 'java').toLowerCase()}
                                original={previousCodeSnippet}
                                modified={selectedCodeEvent?.code_snippet || ''}
                                theme="vs-dark"
                                options={{
                                    readOnly: true,
                                    renderSideBySide: true,
                                    minimap: { enabled: false },
                                    fontSize: 13,
                                    lineNumbers: 'on',
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true
                                }}
                            />
                        ) : (
                            <Editor
                                height="100%"
                                language={(selectedCodeEvent?.language || 'java').toLowerCase()}
                                value={selectedCodeEvent?.code_snippet || '// No hay contenido disponible para este instante.'}
                                theme="vs-dark"
                                options={{
                                    readOnly: true,
                                    minimap: { enabled: false },
                                    fontSize: 13,
                                    lineNumbers: 'on',
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true
                                }}
                            />
                        )}
                    </div>
                </Modal>
            </Content>
        </>
    );
};
